import { koobMarker } from '@koob/map-marker';
import {
  KoobMapCard,
  KoobMapMarker,
  KoobMapStars,
  useKoobMapPopupClose,
} from '@koob/maps';
import { KoobButton } from '@koob/ui';
import { ProductTypeEnum } from '@koob/enums';
import { getHotelBookability, getImageUrl } from '@koob/utils';
import { useSelectedType } from '../../hooks/useTripStoreSelectors';
import type { TripMapHotelCardProps } from './types';

// Hotels on the trip are filled like open pins; suggestions carry a star
// badge and instantly bookable hotels a bolt.
const searchIcon = koobMarker('hotel');
const onTripIcon = koobMarker('hotel', { filled: true });

function hotelPinBadges(hotel: any, suggested: boolean) {
  const badges = [
    suggested && (
      <i key="suggested" className="fa-solid fa-star text-yellow-400" />
    ),
    hotel?.instantAvailability && (
      <i key="instant" className="fa-solid fa-bolt text-green-400" />
    ),
  ].filter(Boolean);

  return badges.length > 0 ? badges : undefined;
}

// Stars, promotions, instant availability and provider, as the list card.
function hotelFacts(hotel: any, hotelCardProps: TripMapHotelCardProps) {
  const promotionCount = hotel?.promotions?.length ?? hotel?.promotionsCount;
  const provider = hotel?.organization?.displayName || hotel?.organization;

  return [
    hotel?.stars > 0 && <KoobMapStars count={hotel.stars} />,
    promotionCount > 0 && (
      <span className="text-green-600">
        <i className="fa-solid fa-tag" /> {promotionCount}
      </span>
    ),
    hotel?.instantAvailability && (
      <span className="text-green-600">
        <i className="fa-solid fa-bolt" />{' '}
        {hotelCardProps.instantAvailabilityLabel}
      </span>
    ),
    provider && (
      <>
        {hotelCardProps.providedByLabel} {provider}
      </>
    ),
  ];
}

interface TripMapHotelPopupProps {
  hotel: any;
  kind?: 'itinerary';
  onAdd: (hotel: any) => void;
  hotelCardProps: TripMapHotelCardProps;
}

// Same card as the experience pins, with what the list's hotel card shows:
// price and promotions, provider, and the same button.
function TripMapHotelPopup({
  hotel,
  kind,
  onAdd,
  hotelCardProps,
}: TripMapHotelPopupProps) {
  const close = useKoobMapPopupClose();
  const selectedType = useSelectedType();
  const { minimumStay, isMinStayUnmet, isBlocked } = getHotelBookability(hotel);
  const styles = (hotel?.styles ?? [])
    .map((style: any) =>
      typeof style === 'string' ? style : style?.displayName,
    )
    .filter(Boolean);

  return (
    <KoobMapCard
      image={getImageUrl(hotel)}
      badge={styles.length > 0 && styles.join(', ')}
      title={hotel?.name ?? hotel?.displayName}
      href={hotelCardProps.getFactsheetHref?.(hotel)}
      subtitle={hotel?.address}
      facts={hotelFacts(hotel, hotelCardProps)}
      warning={
        isBlocked
          ? hotelCardProps.hotelUnavailableLabel
          : isMinStayUnmet && hotelCardProps.minimumStayLabel(minimumStay)
      }
      price={!isBlocked && hotelCardProps.renderPrice(hotel)}
      action={
        kind !== 'itinerary' && (
          <KoobButton
            size="sm"
            variant="primary"
            onClick={() => {
              onAdd(hotel);
              close();
            }}
            disabled={selectedType !== ProductTypeEnum.HOTEL || isBlocked}
          >
            {selectedType === ProductTypeEnum.HOTEL
              ? hotelCardProps.continueLabel
              : hotelCardProps.selectDayLabel}
          </KoobButton>
        )
      }
    />
  );
}

interface TripMapHotelMarkerProps {
  hotel: any;
  onAdd: (hotel: any) => void;
  // A hotel of the trip's programs: a star on its pin.
  suggested?: boolean;
  // A hotel already on the trip, drawn on the itinerary: filled pin, no button.
  kind?: 'itinerary';
  hotelCardProps: TripMapHotelCardProps;
}

export default function TripMapHotelMarker({
  hotel,
  onAdd,
  suggested = false,
  kind,
  hotelCardProps,
}: TripMapHotelMarkerProps) {
  return (
    <KoobMapMarker
      lat={hotel?.lat}
      lng={hotel?.lon}
      title={hotel?.name}
      icon={kind === 'itinerary' ? onTripIcon : searchIcon}
      activeIcon={onTripIcon}
      label={hotelPinBadges(hotel, suggested)}
      popup={
        <TripMapHotelPopup
          hotel={hotel}
          kind={kind}
          onAdd={onAdd}
          hotelCardProps={hotelCardProps}
        />
      }
    />
  );
}
